import { Badge } from 'features/catalog/useBadges';
import { Profile, RawSubmission } from 'features/profiles/useProfiles';
import {
  DEFAULT_BADGE_DATE_ADDED,
  catalogMsNudgedBelowEarlierSameDayClaim,
  parseDateAdded,
} from 'features/catalog/badge-release';
import { BADGE_DESCRIPTIONS } from 'features/catalog/badge-definitions';
import { isBadgeReleased } from 'features/catalog/badge-release';
import {
  asRarityName,
  rarityDividers,
  rarityGhostProfiles,
  profileCountsTowardClaimRate,
} from 'features/catalog/rarity';
import { getFirstPartOfEmail } from 'shared/lib/email';

const sameLocalCalendarDay = (aMs: number, bMs: number): boolean => {
  const a = new Date(aMs);
  const b = new Date(bMs);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

/** Owners counted toward claim % (updated after the feed catalog drop). */
export const badgeClaimRateOwnerCount = (
  badge: Pick<Badge, 'participantIDsWhoOwnThisBadge' | 'claimRateOwnerCount'>
): number => badge.claimRateOwnerCount ?? badge.participantIDsWhoOwnThisBadge?.length ?? 0;

type ClaimRateBadge = Pick<
  Badge,
  | 'id'
  | 'participantIDsWhoOwnThisBadge'
  | 'claimRateOwnerCount'
  | 'claimFloorOwnerCount'
  | 'eligibleClaimProfileCount'
  | 'claimUniverseProfileCount'
  | 'banSelfClaim'
> &
  Partial<Pick<Badge, 'rarityName'>>;

/**
 * Effective claim-rate parts for display + badgeScore.
 *
 * Default: post-catalog owners / profiles with a feed-visible update after the
 * catalog drop (same placement as Badges Feed). Always floored at engaged
 * Claimed-By owners / engaged profiles (5+ badges), so a fresh drop still shows
 * at least e.g. 5/N instead of 0%. Profiles with fewer than 5 badges are omitted
 * from both counts so they don’t skew claimed %.
 *
 * `banSelfClaim` badges are grants (not self-claims), so they always use
 * engaged Claimed-By / engaged profiles — the “since added” eligible pool doesn’t apply.
 * Mythic badges always use that same full-profile rate, never the since-added sample.
 */
export const badgeClaimRateParts = (
  badge: ClaimRateBadge
): {
  owners: number;
  denominator: number;
  rate: number;
  usingOwnersUniverseFloor: boolean;
} => {
  const eligible = badge.eligibleClaimProfileCount ?? 0;
  const universe = badge.claimUniverseProfileCount ?? 0;
  const rateOwners = badgeClaimRateOwnerCount(badge);
  const allOwners = badge.claimFloorOwnerCount ?? badge.participantIDsWhoOwnThisBadge?.length ?? 0;
  const ownersUniverseFloor = universe > 0 ? allOwners / universe : 0;

  // Grants aren't claimed from the feed, and mythics are always measured against
  // every engaged profile — don't use the post-drop eligible sample.
  if (badge.banSelfClaim || badge.rarityName === 'mythic') {
    return {
      owners: allOwners,
      denominator: universe,
      rate: ownersUniverseFloor,
      usingOwnersUniverseFloor: true,
    };
  }

  const eligibleRate = eligible > 0 ? rateOwners / eligible : 0;
  const usingOwnersUniverseFloor = ownersUniverseFloor > eligibleRate;
  if (usingOwnersUniverseFloor) {
    return {
      owners: allOwners,
      denominator: universe,
      rate: ownersUniverseFloor,
      usingOwnersUniverseFloor: true,
    };
  }
  return {
    owners: rateOwners,
    denominator: eligible,
    rate: eligibleRate,
    usingOwnersUniverseFloor: false,
  };
};

/**
 * Claim % for a badge — post-catalog owners / profiles with a feed-visible update
 * after the catalog drop, floored at Claimed-By / all employees.
 * Both counts are precomputed on the Badge at ownership rebuild time (O(1) read).
 */
export const badgeClaimPercent = (badge: ClaimRateBadge): number => Math.round(badgeClaimRateParts(badge).rate * 100);

/** True when the eligible pool is ≤50% of all profiles (small sample → show %*). */
export const isClaimPercentLowSample = (
  badge: ClaimRateBadge,
  totalProfiles = badge.claimUniverseProfileCount ?? 0
): boolean => {
  // Not in the catalog yet — “since added” doesn’t apply (and % is always ~0).
  if (!isBadgeReleased(badge.id)) return false;
  // 0 claimed / 0% is 0% either way — the since-added footnote adds nothing.
  if ((badge.participantIDsWhoOwnThisBadge?.length ?? 0) === 0) return false;
  // Owners/universe floor already uses the full employee universe — no low-sample star.
  if (badgeClaimRateParts(badge).usingOwnersUniverseFloor) return false;
  const eligible = badge.eligibleClaimProfileCount ?? 0;
  return totalProfiles > 0 && eligible <= totalProfiles * 0.5;
};

/** e.g. `20%` or `20%*` when the eligible pool is half or less of all profiles. */
export const formatBadgeClaimPercent = (
  badge: ClaimRateBadge,
  totalProfiles = badge.claimUniverseProfileCount ?? 0
): string => {
  const pct = `${badgeClaimPercent(badge)}%`;
  return isClaimPercentLowSample(badge, totalProfiles) ? `${pct}*` : pct;
};

/** Higher claim % first (uses eligibility-aware rate, including low-sample / starred %). */
export const compareBadgesByClaimPercentDesc = (
  a: ClaimRateBadge & Pick<Badge, 'name'>,
  b: ClaimRateBadge & Pick<Badge, 'name'>
): number => {
  const pctDiff = badgeClaimPercent(b) - badgeClaimPercent(a);
  if (pctDiff !== 0) return pctDiff;
  return a.name.localeCompare(b.name);
};

/**
 * Badge “commonness” score used for rarity / easiest / coolest sorting.
 * Uses owners ÷ eligible (the same rate shown as e.g. `22%*`), projected onto the
 * full profile universe so magnitudes stay comparable to fully-eligible badges.
 */
export const computeBadgeScore = (
  ownersCount: number,
  eligibleCount: number,
  universeCount: number,
  rarityName: string
): number => {
  const rarity = asRarityName(rarityName);
  const rate = eligibleCount > 0 ? ownersCount / eligibleCount : 0;
  const projectedOwners = rate * universeCount;
  return (projectedOwners + rarityGhostProfiles[rarity]) / rarityDividers[rarity];
};

/**
 * Rebuild ownership lists + per-badge eligible denominators.
 *
 * Claim % matches Badges Feed placement:
 * - Catalog drops stamp at local noon on date-only `dateAdded` (or the timed instant).
 * - When a same-day claim lands *before* that stamp, the catalog card is nudged
 *   to just before that claim (see `catalogMsNudgedBelowEarlierSameDayClaim`).
 * - Eligible profiles are those with a feed-visible claim/join submission *after*
 *   that effective catalog time — i.e. the updates that sit above the drop in the feed.
 * - Sheet rows with CUSTOM=`custom` are system grants, not self-claims: they do not
 *   count as a profile update for the “since added” eligible pool (and don’t stamp
 *   earliest-claim for catalog nudges).
 * - Profiles with fewer than 6 claimed badges are omitted from eligible, owner, and
 *   universe counts so they don’t skew claimed %. Claimed By still lists everyone.
 *
 * Claimed By still lists everyone who owns the badge live. Claim % is floored at
 * engaged owners / engaged profiles so brand-new badges don’t show 0% while people already own them.
 */
export const buildEligibleOwnershipBadgesMap = (
  badgesMap: Map<string, Badge>,
  profilesMap: Map<string, Profile>,
  submissionsByEmail: Map<string, RawSubmission[]> = new Map()
): Map<string, Badge> => {
  /** All owners shown in Claimed By (includes stale auto-claim grantees). */
  const ownersByBadge = new Map<string, string[]>();
  for (const [profileID, profile] of profilesMap) {
    for (const badgeID of profile.badgesIDs) {
      if (!badgesMap.has(badgeID)) continue;
      const owners = ownersByBadge.get(badgeID);
      if (owners) owners.push(profileID);
      else ownersByBadge.set(badgeID, [profileID]);
    }
  }

  // Feed-visible submission times per profile + earliest claim time per badge.
  const feedSubmissionMsByProfile = new Map<string, number[]>();
  const earliestClaimMsByBadge = new Map<string, number>();

  for (const [email, submissions] of submissionsByEmail) {
    const profileID = getFirstPartOfEmail(email);
    if (!profilesMap.has(profileID)) continue;

    const sorted = [...submissions].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    const times: number[] = [];
    // Walk only non-custom rows so diffs ignore grant-only submissions between claims.
    const claimSubs = sorted.filter((s) => !s.isCustomGrant);
    claimSubs.forEach((submission, index) => {
      const prev = index === 0 ? null : new Set(claimSubs[index - 1].badgeIDs);
      const newIds = prev ? submission.badgeIDs.filter((id) => !prev.has(id)) : submission.badgeIDs;
      if (newIds.length === 0) return;
      const ms = submission.timestamp.getTime();
      times.push(ms);
      for (const id of newIds) {
        const prevClaim = earliestClaimMsByBadge.get(id);
        if (prevClaim == null || ms < prevClaim) earliestClaimMsByBadge.set(id, ms);
      }
    });
    if (times.length > 0) feedSubmissionMsByProfile.set(profileID, times);
  }

  // Same nudge as Badges Feed: one catalog card per dateAdded stamp; slide only
  // when a same-day claim landed before that stamp (date-only noon vs morning claim).
  const badgesByDateAdded = new Map<string, string[]>();
  for (const badgeId of badgesMap.keys()) {
    const dateAdded = BADGE_DESCRIPTIONS.get(badgeId)?.dateAdded ?? DEFAULT_BADGE_DATE_ADDED;
    const group = badgesByDateAdded.get(dateAdded);
    if (group) group.push(badgeId);
    else badgesByDateAdded.set(dateAdded, [badgeId]);
  }

  const effectiveCatalogMsByBadge = new Map<string, number>();
  for (const [dateAdded, badgeIds] of badgesByDateAdded) {
    const catalogMs = parseDateAdded(dateAdded).getTime();
    let firstClaimMs: number | null = null;
    for (const id of badgeIds) {
      const ms = earliestClaimMsByBadge.get(id);
      if (ms == null || !sameLocalCalendarDay(ms, catalogMs)) continue;
      if (firstClaimMs == null || ms < firstClaimMs) firstClaimMs = ms;
    }
    const effectiveMs = catalogMsNudgedBelowEarlierSameDayClaim(catalogMs, firstClaimMs);
    for (const id of badgeIds) effectiveCatalogMsByBadge.set(id, effectiveMs);
  }

  const engagedProfileIds = new Set<string>();
  for (const [profileID, profile] of profilesMap) {
    if (profileCountsTowardClaimRate(profile)) engagedProfileIds.add(profileID);
  }

  const eligibleProfilesByBadge = new Map<string, Set<string>>();
  for (const badgeId of badgesMap.keys()) {
    eligibleProfilesByBadge.set(badgeId, new Set());
  }
  for (const [profileID, times] of feedSubmissionMsByProfile) {
    if (!engagedProfileIds.has(profileID)) continue;
    const latestSubmissionMs = times[times.length - 1];
    for (const [badgeId, catalogMs] of effectiveCatalogMsByBadge) {
      if (latestSubmissionMs > catalogMs) {
        eligibleProfilesByBadge.get(badgeId)!.add(profileID);
      }
    }
  }

  const next = new Map<string, Badge>();
  const claimUniverseProfileCount = engagedProfileIds.size;
  for (const [badgeID, badge] of badgesMap) {
    const owners = ownersByBadge.get(badgeID) ?? [];
    const eligible = eligibleProfilesByBadge.get(badgeID) ?? new Set<string>();
    let claimRateOwnerCount = 0;
    let claimFloorOwnerCount = 0;
    for (const ownerId of owners) {
      if (!engagedProfileIds.has(ownerId)) continue;
      claimFloorOwnerCount += 1;
      if (eligible.has(ownerId)) claimRateOwnerCount += 1;
    }
    const withOwnership: Badge = {
      ...badge,
      participantIDsWhoOwnThisBadge: owners,
      claimRateOwnerCount,
      claimFloorOwnerCount,
      eligibleClaimProfileCount: eligible.size,
      claimUniverseProfileCount,
    };
    const rate = badgeClaimRateParts(withOwnership);
    next.set(badgeID, {
      ...withOwnership,
      // Same rate shown as e.g. 22%* (or owners/universe floor) — projected onto
      // the full profile universe so scores stay comparable across badges.
      badgeScore: computeBadgeScore(rate.owners, rate.denominator, claimUniverseProfileCount, badge.rarityName),
    });
  }
  return next;
};
